<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection
    class="!mx-0 rounded-lg bg-auth-soft px-1 py-1 dark:bg-white/[0.045] [&>ul]:!gap-0 [&>ul]:!overflow-visible [&>ul]:!px-0"
  >
    <div class="grid gap-0">
      <DropdownItem
        preserve-open
        class="gap-2 !rounded-lg !px-2.5 !py-2 !text-auth-text dark:!text-auth-text-dark"
      >
        <div class="flex min-w-0 flex-grow items-center gap-1">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              class="!border-auth-border !bg-auth-surface !text-auth-text hover:!bg-auth-canvas focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-auth-action dark:!border-white/10 dark:!bg-white/[0.06] dark:!text-auth-text-dark dark:hover:!bg-white/10 dark:focus-visible:!outline-auth-accent [&_svg]:!text-auth-muted dark:[&_svg]:!text-auth-muted-dark"
              @click="toggle"
            >
              <div class="flex min-w-0 items-center gap-1 text-sm">
                <div class="flex-shrink-0 p-1">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span class="truncate max-w-[7rem]">
                  {{ activeStatus.label }}
                </span>
              </div>
            </Button>
          </template>
          <DropdownBody
            class="z-20 min-w-32 [&>.n-dropdown-body]:!gap-1 [&>.n-dropdown-body]:!border-auth-border [&>.n-dropdown-body]:!bg-auth-surface [&>.n-dropdown-body]:!shadow-xl [&>.n-dropdown-body]:!shadow-auth-brand/10 [&>.n-dropdown-body]:!backdrop-blur-none dark:[&>.n-dropdown-body]:!border-auth-border-dark dark:[&>.n-dropdown-body]:!bg-auth-surface-dark dark:[&>.n-dropdown-body]:!shadow-black/30"
          >
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer !rounded-lg !text-auth-text hover:!bg-auth-soft focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-auth-action dark:!text-auth-text-dark dark:hover:!bg-white/[0.07] dark:focus-visible:!outline-auth-accent"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem
        class="!rounded-none !border-t !border-auth-border !px-2.5 !py-2.5 !text-auth-text dark:!border-white/10 dark:!text-auth-text-dark"
      >
        <div class="min-w-0 flex-grow">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="ms-1 inline-block size-4 align-middle text-auth-muted dark:text-auth-muted-dark"
          />
        </div>
        <ToggleSwitch
          v-model="autoOfflineToggle"
          class="focus:!ring-auth-action focus:!ring-offset-auth-surface dark:focus:!ring-auth-accent dark:focus:!ring-offset-auth-brand-dark [&[aria-checked='true']]:!bg-auth-action dark:[&[aria-checked='true']]:!bg-auth-accent"
        />
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
